import { haversineDistance } from "../../../../utils/locationUtils"

const getLowestDecimalPlaces = (numbers) => {

    let lowestPlaces

    for(const number of numbers){
    
        const numStr = number.toString();

        if (numStr.includes('.')) {
            const currentPlaces = numStr.split('.')[1].length;

            if(lowestPlaces === undefined || currentPlaces < lowestPlaces){
                lowestPlaces = currentPlaces
            }
        } else {
            lowestPlaces = 0
        }
    }

    return lowestPlaces
  }

/**
 * validateRealChain - takes in two agms and compares the distance between the points calculated from real chain data, and the distance calculated using the lat/long values using the haversine formula
 * 
 * NOTES: if distance from real chain values is shorter than the haversine distance, it may be incorrect, so we want to give the user the opportunity to correct it
 * because real chain can be longer than distance between two lat/long points, due to subchains, but it can never be shorter
 * distances are in km
 * 
 * @param {Object} agm1 
 * @param {Object} agm2 
 * @returns {Object | null}
 */
export const validateRealChain = (agm1, agm2) => {

    //get lat and lng for both agms - use real lat/lng if available, otherwise use estimated
    const agm1Lat = agm1.Real_Lat ? agm1.Real_Lat : agm1.Est_Lat
    const agm1Lng = agm1.Real_Lng ? agm1.Real_Lng : agm1.Est_Lng

    const agm2Lat = agm2.Real_Lat ? agm2.Real_Lat : agm2.Est_Lat
    const agm2Lng = agm2.Real_Lng ? agm2.Real_Lng : agm2.Est_Lng

    //get the number of decimal places to use in calculation - based off of the value with the lowest number of decimal places
    const decimalPlaces = getLowestDecimalPlaces([agm2.Real_Chain, agm1.Real_Chain, agm1Lat, agm1Lng, agm2Lat, agm2Lng])

    //calculate distances
    const realChainDist = Number((agm2.Real_Chain - agm1.Real_Chain).toFixed(decimalPlaces))

    const latLongDist = Number(haversineDistance(agm1Lat, agm1Lng, agm2Lat, agm2Lng).toFixed(decimalPlaces))

    //if realChainDist is shorter, return the data so it can be displayed to user
    if(realChainDist < latLongDist){
        return {
            agm1: agm1.Site_Name,
            agm2: agm2.Site_Name,
            realChainDist,
            latLongDist
        }
    }

    return null
}

export const validateEstChain = (agm1, agm2) => {

    //get lat and lng for both agms - use real lat/lng if available, otherwise use estimated
    const agm1Lat = agm1.Real_Lat ? agm1.Real_Lat : agm1.Est_Lat
    const agm1Lng = agm1.Real_Lng ? agm1.Real_Lng : agm1.Est_Lng

    const agm2Lat = agm2.Real_Lat ? agm2.Real_Lat : agm2.Est_Lat
    const agm2Lng = agm2.Real_Lng ? agm2.Real_Lng : agm2.Est_Lng

    //get the number of decimal places to use in calculation - based off of the value with the lowest number of decimal places
    const decimalPlaces = getLowestDecimalPlaces([agm2.Est_Chain, agm1.Est_Chain, agm1Lat, agm1Lng, agm2Lat, agm2Lng])

    //calculate distances
    const estChainDist = Number((agm2.Est_Chain - agm1.Est_Chain).toFixed(decimalPlaces))

    const latLongDist = Number(haversineDistance(agm1Lat, agm1Lng, agm2Lat, agm2Lng).toFixed(decimalPlaces))

    //if realChainDist is shorter, return the data so it can be displayed to user
    if(estChainDist < latLongDist){
        return {
            agm1: agm1.Site_Name,
            agm2: agm2.Site_Name,
            estChainDist,
            latLongDist
        }
    }

    return null
}

export const checkValidNumbers = (agmData) => {
    const errors = [];

    // make sure all values being saved in Real_Chain are numbers and zero only allowed on first row
    agmData.forEach((agm, idx) => {
      const value = Number(agm.Real_Chain);
      if (isNaN(value) || agm.Real_Chain === null) {
        errors.push({ row: idx, column: 'Real_Chain', message: 'must be a number' });
      } else if (value < 0) {
        errors.push({ row: idx, column: 'Real_Chain', message: 'must be >= 0' });
      } else if (value === 0 && idx > 0) {
        // only the first row may legitimately have zero
        errors.push({ row: idx, column: 'Real_Chain', message: 'zero only allowed on first row' });
      }
    });

    return errors;
  }

  export const checkDeltaGreaterThanZero = (agmData) => {
    const errors = [];
    agmData.forEach((agm, idx) => {
      const delta = Number(agm.Real_Chain_Delta);
      // first row is allowed to be zero; others must be > 0
      if (isNaN(delta) || (idx > 0 && delta <= 0)) {
        errors.push({ row: idx, column: 'Real_Chain_Delta', message: 'must be > 0' });
      }
    });
    return errors;
  }

  export const checkNumberMissing = (agmData) => {
    const errors = [];
    agmData.forEach((agm, idx) => {
      if (agm.Real_Chain === null || agm.Real_Chain === "" || isNaN(Number(agm.Real_Chain))) {
        errors.push({ row: idx, column: 'Real_Chain', message: 'value required' });
      }
    });
    return errors;
  }

  export const convertRealChainToNumber = (agmData) => {
    try {
      agmData.forEach(agm => {
        agm.Real_Chain = agm.Real_Chain !== "" && agm.Real_Chain != null ? Number(agm.Real_Chain) : null
        agm.Real_Chain_Delta = agm.Real_Chain_Delta !== "" && agm.Real_Chain_Delta != null ? Number(agm.Real_Chain_Delta) : null
      });
      return agmData;
    } catch (error) {
      console.error("Error converting Real_Chain values:", error);
      return false;
    }
  }